/**
 * The shape of the flight deck.
 *
 * Extracted from the renderer because it is not only a shape to draw — it is
 * the answer to "is there steel under this point", and the parking check needs
 * that as much as the mesh does. Nine aeroplanes were originally parked at
 * positions that cleared the landing area perfectly well and were, six of
 * them, hanging over open water off the starboard side, because "clear of the
 * wires" and "on the ship" are two different questions and only one of them
 * was being asked.
 *
 * Points are [starboard, forward] in feet from midships on the centreline —
 * the SHIP frame. The deck is markedly wider to port than to starboard,
 * because the angled landing area sponsons out that side.
 */

export const DECK_OUTLINE = [
  [12, 560],
  [44, 520],
  [62, 430],
  [74, 300],
  [78, 60],
  [78, -200],
  [72, -420],
  [62, -545],
  [-48, -545],
  [-66, -430],
  [-86, -250],
  [-104, -60],
  [-118, 90],
  [-126, 230],
  [-118, 330],
  [-96, 400],
  [-62, 470],
  [-24, 540],
]

/**
 * Is this ship-frame point over the flight deck, with `marginFt` to spare all
 * the way round?
 */
export function isOnFlightDeck(xFt, yFt, marginFt = 0) {
  if (!pointInDeck(xFt, yFt)) return false
  if (marginFt <= 0) return true
  // Sample a ring rather than scaling the polygon. Shrinking a polygon toward
  // its centroid is not an offset — at the bow, where the deck is 560 ft from
  // the centroid, a 16 ft margin pulled the outline in by 75 ft and rejected
  // spots that were comfortably aboard.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2
    if (!pointInDeck(xFt + Math.cos(a) * marginFt, yFt + Math.sin(a) * marginFt)) return false
  }
  return true
}

/** Ray casting. Odd crossings means inside — handles the concave port quarter. */
function pointInDeck(xFt, yFt) {
  let inside = false
  for (let i = 0, j = DECK_OUTLINE.length - 1; i < DECK_OUTLINE.length; j = i++) {
    const [si, fi] = DECK_OUTLINE[i]
    const [sj, fj] = DECK_OUTLINE[j]
    const hit = si > yFt !== sj > yFt && xFt < ((fj - fi) * (yFt - si)) / (sj - si) + fi
    if (hit) inside = !inside
  }
  return inside
}

/** How far to starboard the deck extends at this station, feet. */
export function deckEdgeStarboardFt(xFt) {
  let best = -Infinity
  for (let i = 0; i < DECK_OUTLINE.length; i++) {
    const [s1, f1] = DECK_OUTLINE[i]
    const [s2, f2] = DECK_OUTLINE[(i + 1) % DECK_OUTLINE.length]
    if ((f1 - xFt) * (f2 - xFt) > 0) continue
    const t = Math.abs(f2 - f1) < 1e-6 ? 0 : (xFt - f1) / (f2 - f1)
    best = Math.max(best, s1 + (s2 - s1) * t)
  }
  return best
}
